import { useDispatch } from 'react-redux';
import { pick, prop } from 'ramda';
import { usePushSubscription } from 'server';

import { useMountEffect } from '@core/js/hooks/useMountEffect';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useRecentlyGamesSelector } from '@interlayer/js/app/store/hooks/useRecentlyGames';
import { gamesPushAction, setRecentlyPlayedAction } from '@interlayer/js/app/store/reducers/recentlyGames/actions';
import { getRecentlyPlayed } from '@interlayer/js/app/store/reducers/recentlyGames/server';
import { TGamesPushAction } from '@interlayer/js/app/store/reducers/recentlyGames/types';
import { useFilterFeedBlacklist } from '@interlayer/js/components/system/App/hooks/useGetGamesConfiguration/helpers';

export const useRecentlyGames = (includeRecentlyPlayed = true) => {
    const dispatch = useDispatch();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { gamesConfiguration, gamesFeeds } = useAppSelector(pick(['gamesConfiguration', 'gamesFeeds']));
    const { recentlyPlayedGames, recentlyPlayedLoaded } = useRecentlyGamesSelector();
    const filterFeedBlacklist = useFilterFeedBlacklist();

    usePushSubscription(31133, () => (pushMessage: TGamesPushAction) => dispatch(gamesPushAction(pushMessage)));

    useMountEffect((isMounted: () => boolean) => {
        if (!includeRecentlyPlayed || !isLoggedIn || recentlyPlayedLoaded || !Object.keys(gamesConfiguration).length) {
            return;
        }

        getRecentlyPlayed()
            .then((response = {}) => {
                if (isMounted()) {
                    const { data: { tags = [] } = {} } = response;
                    const { value: gameCodes } = tags.find(tag => tag.name === 'uam/hidden/playerRecentGames') || {};
                    const parsedGameCodes: string[] = typeof gameCodes === 'string'
                        ? JSON.parse(gameCodes)
                        : gameCodes;

                    const filteredGameList = filterFeedBlacklist(parsedGameCodes, gamesFeeds);
                    dispatch(setRecentlyPlayedAction(filteredGameList
                        ?.filter((gameCode: string | number) => gamesConfiguration?.[gameCode]))
                    );
                }
            })
            .catch(console.error);
    }, [gamesConfiguration, isLoggedIn, includeRecentlyPlayed, dispatch]);

    return recentlyPlayedGames;
};
